import { useCallback, useEffect, useState } from 'react'

import type { ApiResult, Resource } from '../../api/client.ts'
import { useApiResource } from '../../api/useApiResource.ts'
import {
  fetchPayloadWebhookAttempts,
  fetchPayloadWebhookSummary,
  fetchWebhookAttempts,
  fetchWebhookConfig,
  fetchWebhookSummary,
  type AttemptStatusFilter,
  type PayloadWebhookAttempt,
  type PayloadWebhookSummary,
  type WebhookAttempt,
  type WebhookEndpointConfig,
  type WebhookSummary,
} from '../../api/webhooks.ts'
import { useApiFetch } from '../../ghost/GhostProvider.tsx'

// Each of the reads owns its own state (api/useApiResource.ts).
//
// They fail independently on purpose: /summary is the most likely to be slow (it
// aggregates), and a dashboard that blanks the attempts table because a count
// query timed out is worse than one that shows the table with the counts missing.
//
// All of them read through `useApiFetch` (src/ghost) rather than the auth
// provider's own `apiFetch`, so a Curri admin viewing the dashboard as a partner
// gets that partner's data. Its identity changes with the ghost target and every
// hook below already lists it as a dependency, which is what makes entering and
// leaving ghost mode refetch on its own.

export const useWebhookConfig = (): {
  resource: Resource<WebhookEndpointConfig[]>
  /** Refetch — called after an endpoint edit so the card shows what the server
   *  now holds rather than what the editor believes it submitted. */
  refresh: () => void
} => {
  const apiFetch = useApiFetch()
  const [generation, setGeneration] = useState(0)
  const resource = useApiResource(
    signal => fetchWebhookConfig(apiFetch, signal),
    [apiFetch, generation]
  )
  const refresh = useCallback(() => setGeneration(current => current + 1), [])
  return { resource, refresh }
}

export const useWebhookSummary = (
  windowDays: number
): Resource<WebhookSummary> => {
  const apiFetch = useApiFetch()
  return useApiResource(
    signal => fetchWebhookSummary(apiFetch, windowDays, signal),
    [apiFetch, windowDays]
  )
}

export const usePayloadWebhookSummary = (
  windowDays: number
): Resource<PayloadWebhookSummary> => {
  const apiFetch = useApiFetch()
  return useApiResource(
    signal => fetchPayloadWebhookSummary(apiFetch, windowDays, signal),
    [apiFetch, windowDays]
  )
}

interface KeysetFeed<T> {
  resource: Resource<T[]>
  /** Null once the last page has been read. */
  nextCursor: number | null
  isLoadingMore: boolean
  loadMore: () => void
  /** Set when a load-more failed; the rows already shown stay put. */
  loadMoreFailed: boolean
}

/**
 * A cursor-paged list, owned as its own state: the first page replaces, every
 * later page appends, and a failed page leaves what is already on screen alone.
 *
 * `load` takes the cursor rather than closing over it so the hook owns paging
 * entirely; `deps` are the read's real identity, exactly as in useApiResource.
 */
const useKeysetFeed = <T,>(
  load: (
    before: number | undefined,
    signal: AbortSignal
  ) => Promise<ApiResult<{ items: T[]; nextCursor: number | null }>>,
  deps: unknown[]
): KeysetFeed<T> => {
  const [resource, setResource] = useState<Resource<T[]>>({ status: 'loading' })
  const [nextCursor, setNextCursor] = useState<number | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [loadMoreFailed, setLoadMoreFailed] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setResource({ status: 'loading' })
    setNextCursor(null)
    setLoadMoreFailed(false)

    void (async () => {
      try {
        const result = await load(undefined, controller.signal)
        if (controller.signal.aborted) return
        if (!result.ok) {
          setResource({ status: 'failed', failure: result.failure })
          return
        }
        setResource({ status: 'ready', data: result.data.items })
        setNextCursor(result.data.nextCursor)
      } catch {
        // Aborted.
      }
    })()

    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  const loadMore = useCallback(() => {
    if (nextCursor === null || isLoadingMore) return

    setIsLoadingMore(true)
    setLoadMoreFailed(false)

    void (async () => {
      const controller = new AbortController()
      try {
        const result = await load(nextCursor, controller.signal)
        if (!result.ok) {
          setLoadMoreFailed(true)
          return
        }
        // Append rather than replace, and keep the existing rows if the state
        // moved on — a failed page must not discard what the reader already has.
        setResource(current =>
          current.status === 'ready'
            ? { status: 'ready', data: [...current.data, ...result.data.items] }
            : current
        )
        setNextCursor(result.data.nextCursor)
      } catch {
        setLoadMoreFailed(true)
      } finally {
        setIsLoadingMore(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nextCursor, isLoadingMore])

  return { resource, nextCursor, isLoadingMore, loadMore, loadMoreFailed }
}

/** Both attempt pages are `{ attempts, nextCursor }`; the feed names it `items`. */
const asFeedPage = <T,>(
  result: ApiResult<{ attempts: T[]; nextCursor: number | null }>
): ApiResult<{ items: T[]; nextCursor: number | null }> =>
  result.ok
    ? {
        ok: true,
        data: { items: result.data.attempts, nextCursor: result.data.nextCursor },
      }
    : result

export const useWebhookAttempts = (params: {
  windowDays: number
  status: AttemptStatusFilter
  /** Pins the feed to one delivery; undefined reads every delivery in the window. */
  deliveryExternalId?: string
}): KeysetFeed<WebhookAttempt> => {
  const apiFetch = useApiFetch()
  const { windowDays, status, deliveryExternalId } = params

  return useKeysetFeed(
    async (before, signal) =>
      asFeedPage(
        await fetchWebhookAttempts(
          apiFetch,
          { windowDays, status, before, deliveryExternalId },
          signal
        )
      ),
    [apiFetch, windowDays, status, deliveryExternalId]
  )
}

export interface PayloadAttemptsFeed {
  resource: Resource<PayloadWebhookAttempt[]>
  nextCursor: number | null
  isLoadingMore: boolean
  loadMore: () => void
  loadMoreFailed: boolean
}

export const usePayloadWebhookAttempts = (params: {
  windowDays: number
  status: AttemptStatusFilter
}): PayloadAttemptsFeed => {
  const apiFetch = useApiFetch()
  const { windowDays, status } = params

  return useKeysetFeed(
    async (before, signal) =>
      asFeedPage(
        await fetchPayloadWebhookAttempts(
          apiFetch,
          { windowDays, status, before },
          signal
        )
      ),
    [apiFetch, windowDays, status]
  )
}
